"use client";

import type { Entry } from "contentful";
import Image from "next/image";
import { trackFooterNavItemClicked } from "@/lib/analytics/segment";
import { getEntriesOfContentType } from "@/lib/contentful/guards";
import type { TypeFooterSkeleton, TypeSocialMediaIconSkeleton } from "@/types/contentful/generated";
import { ContentfulHelper } from "@/utils/ContentfulHelper";
import { CmsLink } from "./CmsLink";

type SocialIconEntry = Entry<TypeSocialMediaIconSkeleton, undefined, string>;
type FooterFields = Entry<TypeFooterSkeleton, undefined, string>["fields"];

interface Props {
  socialMediaIcons: FooterFields["socialMediaIcons"];
}

function hostnameLabel(rawUrl: string): string {
  try {
    return new URL(rawUrl).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function FooterSocialLinks({ socialMediaIcons }: Props) {
  const icons = getEntriesOfContentType<SocialIconEntry>(socialMediaIcons, "socialMediaIcon");

  if (icons.length === 0) return null;

  return (
    <div className="flex justify-center gap-6 px-6 py-5 md:px-12">
      {icons.map((icon, index) => {
        const img = ContentfulHelper.parseContentfulContentImage(icon.fields.iconImage);
        if (!img) return null;
        const url = icon.fields.destinationUrl?.trim();
        if (!url) return null;
        // Customer-safe label: prefer the Contentful asset description, fall
        // back to the destination URL's hostname. internalTitle is editor-only
        // and must never reach an alt attribute or an analytics payload.
        const label = img.alt || hostnameLabel(url);
        return (
          <CmsLink
            key={`${icon.sys.id}-${index}`}
            destination={url}
            isNewTab
            className="no-underline!"
            onNavigate={href => trackFooterNavItemClicked({ label, destinationUrl: href })}
          >
            <Image src={img.src} alt={label} width={24} height={24} className="h-6 w-6" />
          </CmsLink>
        );
      })}
    </div>
  );
}
